import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Input, Modal, Tooltip } from 'antd';
import { ClipboardCheck, History, Lock } from 'lucide-react';
import { useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import type { CandidateDto, CopyProposalDto, FillSheetDto, NotifyResultDto, OfficerSummaryDto } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill } from '@/components/StatusPill';
import { RaiserPill } from '@/components/RaiserPill';
import { FillLines } from '@/components/fill/FillLines';
import { CommandFamilies } from '@/components/fill/CommandFamilies';
import { SharedLines } from '@/components/fill/SharedLines';
import { FILL_COPY } from '@/components/fill/copy';
import { DelegateInline } from '@/components/fill/DelegateInline';
import type { PostMode } from '@/components/fill/PostModeControl';
import { DutyHistoryDrawer } from '@/components/fill/DutyHistoryDrawer';
import { OfficerSearchSheet } from '@/components/fill/OfficerSearchSheet';
import { useAuth } from '@/hooks/useAuth';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import {
  can,
  fillAudienceFor,
  mayCompleteShortfall,
  mayDelegateAllocation,
  actingOrgUnitTypeOf,
  READ_ONLY_TOOLTIP,
} from '@/lib/accessRole';
import { Loading, Skeleton } from '@/components/Skeleton';
import { fillControlsFor } from '@/lib/fillPermissions';
import { sheetDischarged } from '@/lib/fillPost';
import { awaitedCommands, commandObligation, withCommandFamilies } from '@/lib/commandFamilies';
import { refusalFor } from '@/components/fill/copy';
import { api, type ApiError } from '@/lib/api';
import {
  countNamed,
  countOutstanding,
  countSlots,
  isStaffable,
  namedOfficerIds,
  nextEmptySlot,
  slotPosition,
  type FillCursor,
} from '@/lib/fillCursor';
import { fillFooter } from '@/lib/statusPresentation';
import { closedShortReasonsOf } from '@/lib/shortfallNote';
import { queryKeys } from '@/lib/queryKeys';
import { useFillSheet } from '@/components/fill/useFillSheet';
import { DUTY_FILL_COPY } from './copy';
import { CopyToOtherDates } from './components/CopyToOtherDates';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE FILL SCREEN — PA_REQUIREMENTS §6.2, and the screen this pass is judged on. [P4]
 *
 *  ⚠️ THE HOT PATH INVALIDATES NOTHING, AND THAT IS STRUCTURAL RATHER THAN CAREFUL.
 *
 *  P3b.3 lost data because a mutation invalidated `queryKeys.arrangements.all` — a PREFIX of
 *  the record being edited — so the record refetched and a hydration effect wrote the
 *  server's copy over what was being typed. This screen takes up to twelve writes in ninety
 *  seconds, which is twelve chances to fire the same bug.
 *
 *  So: the sheet lives under `queryKeys.fill`, a namespace nothing else can reach; every
 *  assign, swap and remove writes with `setQueryData` and invalidates NOTHING; and the two
 *  count-bearing namespaces are flushed ONCE, on the way out. `setQueryData` neither
 *  refetches nor marks stale, so there is no refetch for a local write to race — the
 *  MECHANISM is removed, not just an instance of it.
 *
 *  ⚠️ THE ROLLBACK IS ONE SLOT, NEVER THE SHEET. React Query's canonical `onMutate` pattern
 *  snapshots the whole record and restores it on error, which here would undo the eleven
 *  other assignments still in flight. Same shape as P3b.3, smaller blast radius, and it is
 *  the version that ships by habit.
 *
 *  ⚠️ THE PAGE OWNS ITS SCROLLING. Root `h-full min-h-0`, ONE `flex-1 overflow-y-auto` child,
 *  and the footer is a `shrink-0` SIBLING of it. Never `sticky bottom-0`: a sticky element
 *  is in normal flow, so `<main>`'s own `py-6` stays below it as dead space and mid-scroll it
 *  floats over the content instead of bounding it. `ArrangementBuilder` learned this from a
 *  browser, and jsdom cannot see it — the structural classes ARE the mechanism.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [amended C3c.1] ONE TEST ID PER WINDOW STATE, and the two that existed keep their names —
 * `naming-window-closed` and `duty-window-closed` are already asserted in `DutyFill.test.tsx`
 * and renaming either would be a green suite over a moved control.
 */
const WINDOW_BANNER_TESTID = {
  namingClosed: 'naming-window-closed',
  /*
   * ⚠️ [amended C3c.1] `swap-closed-banner`, NOT `swap-window-closed` — THAT ID IS ALREADY TAKEN,
   * BY A DIFFERENT PREDICATE.
   *
   * `ArrangementDetail/components/DutyOfficers.tsx` renders `swap-window-closed` whenever the swap
   * window is SHUT, which covers `swapClosed` AND `allClosed`. This banner fires only on the
   * inverted arm. Two components, one id, two meanings — and no test fails today only because the
   * two never render in one tree. A browser assertion would find whichever came first.
   */
  swapClosed: 'swap-closed-banner',
  allClosed: 'duty-window-closed',
} as const satisfies Record<'namingClosed' | 'swapClosed' | 'allClosed', string>;

const DutyFillPage = () => {
  const { dutyId = '' } = useParams<{ dutyId: string }>();
  const { officer } = useAuth();
  const queryClient = useQueryClient();
  const { message, modal, notification } = App.useApp();

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [PRE-P6.2] `notification` AT `placement: 'bottom'`, NOT `message`.
   *
   *  AntD's `message` has no placement — only a `top` offset, and that offset lives on the
   *  ONE container every message in the app shares, so moving it would move every toast on
   *  every screen. `notification` takes a placement as an argument and `'bottom'` is
   *  centred at the foot of the viewport, which is where this belongs: the operator's eye is
   *  on the chip row they just filled, and a banner at the top of a scrolled page is a
   *  message nobody reads.
   *
   *  ⚠️ IT IS FROM `App.useApp()`, LIKE EVERY OTHER TRANSIENT HERE. §8: the STATIC
   *  `notification.*` escapes `ThemeScope` and does not render at all under jsdom, so a
   *  static one would be untestable as well as unthemed.
   *
   *  ⚠️ AND IT IS DELIBERATELY NOT A `modal`. Nothing has gone wrong and nothing needs
   *  acknowledging — the operator is mid-burst, and a dialog would cost the click this
   *  change exists to remove.
   */
  const notifyRowFilled = (): void => {
    notification.info({
      message: DUTY_FILL_COPY.rowFilled,
      description: DUTY_FILL_COPY.rowFilledHint,
      placement: 'bottom',
      duration: 4,
    });
  };

  const readOnly = officer ? !can(officer.accessRole, 'arrangement.write') : true;

  const [cursor, setCursor] = useState<FillCursor | null>(null);
  const [staged, setStaged] = useState<Map<string, OfficerSummaryDto>>(new Map());
  const [copiedFrom, setCopiedFrom] = useState<CopyProposalDto['source']>(null);
  const [swapReasonFor, setSwapReasonFor] = useState<{
    assignmentId: string;
    officerId: string;
    /** ⚠️ [C1a §3] Carried so the confirmed swap can evict the outgoing officer, exactly as
        the unconfirmed one does. Without it this path re-opened the defect on a COMPLETE share. */
    slotRef: string;
    officer: OfficerSummaryDto;
  } | null>(null);
  const [swapReason, setSwapReason] = useState('');
  /* ⚠️ [C3c.2] The short-fall dialog. Two atoms, cleared together on both exits — see the
     modal at the foot of the file, and `swapReasonFor` above for the idiom being copied. */
  const [shortfallOpen, setShortfallOpen] = useState(false);
  const [shortfallReason, setShortfallReason] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  /**
   * Slots this sitting has picked but whose write may not have answered yet. See `onPick`.
   * Never rendered; only the cursor consults it.
   */
  const inFlight = useRef<Map<string, OfficerSummaryDto>>(new Map());
  /**
   * ⚠️ [PRE-P6 §2b] A RENDER-VISIBLE MIRROR OF `inFlight`, AND THE DUPLICATION IS THE POINT.
   *
   * The ref above must stay a ref: `onPick` writes it and then reads the accumulated map
   * SYNCHRONOUSLY on the very next keystroke, which a `useState` closure cannot do. But
   * greying a named officer in the search list is a RENDER, and a ref schedules none — so
   * until now an officer went grey only once their write answered AND the candidate query
   * refetched, which on a slow link is several keystrokes later, and on the same slot never.
   *
   * One write site keeps them in step: the state is a snapshot of the ref, taken where the
   * ref is written and nowhere else. The ref remains the ONLY input to cursor arithmetic.
   */
  const [picked, setPicked] = useState<ReadonlyMap<string, OfficerSummaryDto>>(new Map());

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [amended C1a §3] THE ONE WRITER FOR BOTH HALVES, AND IT CAN NOW REMOVE.
   *
   *  `picked` had exactly one write site and NO removal site at all: it was added to when an
   *  officer was named and never emptied. That made C1a §3 — swap A out for B, then try to
   *  put A back, and A is greyed and `select()`-refused until the page is reloaded. The
   *  SERVER is right at every layer (`SWAPPED_OUT` is outside the partial unique index, and
   *  `candidate.service.ts` filters on `status: 'ASSIGNED'`), so only the client refused.
   *
   *  ⚠️ THE MAP IS KEYED BY `slotRef`, WHICH IS WHY A SWAP NEEDS NO DELETE. Writing the
   *  INCOMING officer onto the slot evicts the outgoing one by construction — one operation,
   *  no chance of the two halves disagreeing. `remove` passes `undefined` and deletes.
   *
   *  ⚠️ AND THE REF IS STILL THE ONLY INPUT TO CURSOR ARITHMETIC. The state is a snapshot
   *  taken here and nowhere else — see the mirror's declaration above.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const rememberPick = (slotRef: string, officer: OfficerSummaryDto | undefined): void => {
    if (officer) inFlight.current.set(slotRef, officer);
    else inFlight.current.delete(slotRef);
    setPicked(new Map(inFlight.current));
  };
  /*
   * ⚠️ [amended D4.3] THE SHEET AND ITS THREE PER-SLOT WRITES MOVED TO `useFillSheet`.
   *
   * Not a tidy-up: the arrangement detail page grew an officer surface that also Replaces and
   * Removes, and a second copy of these mutations would be a second definition of what a swap
   * does. Every rule P4 recorded — no invalidation on the hot path, a ONE-SLOT rollback, the
   * flush on the way out — now lives in one file that both screens call, and
   * `lib/fillKeyIsolation.test.ts` names that file so the rule survives an edit to it.
   */
  /**
   * ⚠️ [D4.5 §3e] ONE DIVISION'S SHARE, WHEN THE INBOX ROW ASKED FOR ONE.
   *
   * `?division=` is a QUERY PARAMETER and never a route change: `/duties/:dutyId` is reached
   * from the inbox, the arrangement detail page and a bookmark, so changing its shape would
   * break every existing link. Absent, this screen is byte-identical to what it was.
   *
   * Read ONCE here and handed to both the sheet and the completion, so the screen and the
   * button cannot disagree about whose duty is being closed.
   */
  const [searchParams] = useSearchParams();
  const division = searchParams.get('division') ?? undefined;

  /*
   * ⚠️ [amended C1a] `division` IS PART OF THE KEY, AND OMITTING IT WROTE INTO A DEAD ENTRY.
   *
   * `useFillSheet` builds the LIVE query key as `queryKeys.fill.sheet(dutyId, division)`
   * ([D4.5] §3e), and this local one did not carry the division at all — so on a narrowed
   * sheet (`/duties/:id?division=CD`) `saveStaged` and `complete` wrote their fresh
   * `FillSheetDto` into `['fill','sheet',dutyId]` while the mounted query read
   * `['fill','sheet',dutyId,'CD']`. The footer never flipped to COMPLETE and the staged
   * chips never cleared from server data.
   *
   * It is the exact twin of what [PRE-P6.2] fixed in `refreshSheet` — that one READ the
   * whole duty and wrote it into the narrowed key; this one wrote the right data into the
   * un-narrowed one. Same key, one argument, both directions.
   */
  const sheetKey = queryKeys.fill.sheet(dutyId, division);
  /** [C1b §3] Which allocation row the split modal is about, or `null`. */
  /**
   * ⚠️ [C1f §1a] WHICH POSTS ARE SHOWING DELEGATE RATHER THAN NAME, by `<lineKey>|<allocKey>`.
   *
   * It is the HOST's state because the host owns the write — `FillLines` still holds no query
   * and no mutation. It is deliberately not persisted: a mode is a half-finished decision, and
   * restoring one on a reload would show a delegate row for a post the operator has since had
   * filled by somebody else.
   */
  const [postMode, setPostMode] = useState<ReadonlyMap<string, PostMode>>(new Map());

  const { sheet, data, markDirty, flushCounts, refreshSheet, assign, swap, remove } = useFillSheet(dutyId, {
    division,
    /* ⚠️ [C1a §3] A refused write must release the officer it was about, or the operator
       cannot retry the very name that failed. See `rememberPick`. */
    onSlotRolledBack: (slotRef) => {
      rememberPick(slotRef, undefined);
    },
  });
  const lines = data?.lines ?? [];
  /* [C5c §2h] The columns this duty is covered for from another duty point — read-only. */
  const sharedLines = data?.sharedLines ?? [];
  /*
   * ⚠️ [C6a §4] THE COMMAND ROLES ASKED OF A DIVISION ON THIS DUTY POINT — present only when there is
   * one the caller can see, and NEVER a line: `lines` above and every figure computed from it (`total`,
   * `named`, `outstanding`, the footer) are the force alone. See `components/fill/CommandFamilies.tsx`.
   */
  const commandFamilies = data?.commandFamilies ?? [];

  const saveStaged = useMutation({
    mutationFn: () =>
      api.post<FillSheetDto>(`/duties/${dutyId}/assignments/bulk`, {
        assignments: [...staged.entries()].map(([slotRef, chosen]) => ({ slotRef, officerId: chosen.id })),
      }),
    onSuccess: (fresh) => {
      queryClient.setQueryData<FillSheetDto>(sheetKey, fresh);
      setStaged(new Map());
      setCopiedFrom(null);
      markDirty();
      void message.success('Names saved');
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3c.1 §6b] SEND NOTIFICATION, IN THE SLOT `Mark complete` USED TO HOLD.
   *
   *  §6a hides `Mark completed` because FR-5.3.6 auto-completes the moment the last officer is
   *  named, so the button was already `disabled` at exactly the state it described.
   *
   *  ⚠️ AND THAT REMOVES THE SHORTFALL PATH. [P4] let a division complete at PARTIAL **with a
   *  reason**, which is how it recorded that it could not supply the force it was asked for.
   *  Nothing replaces it — the client has parked it deliberately, and it is in the CHANGELOG so
   *  it is not lost. `POST /duties/:id/completions`, `completeShares` and `shortfall_reason` are
   *  all UNTOUCHED, so restoring the control is a button and a modal against a contract that
   *  never moved.
   *
   *  ⚠️ THE ENABLE PREDICATE IS THE CLIENT'S OWN `named === total`, NOT `fillState`.
   *  `autoCompleteFullShares` makes `'NAMED'` a state this sheet essentially never shows, and
   *  `'COMPLETE'` is ALSO true of a share closed at partial with a shortfall — which is exactly
   *  the duty §6b must refuse to call fully staffed. `slotSequence` already excludes vehicles
   *  ([C2a §5a]: red-amber-green answers "is this staffed", which a van cannot be), and the
   *  server excludes them too, so the button and the refusal agree.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const notify = useMutation({
    mutationFn: () =>
      /* ⚠️ [D4.5 §3e] The SAME division the sheet was narrowed to — `completeShares`' own rule:
         sending nothing here while the sheet showed one division would notify every division
         the caller can see, which is right for a nodal and wrong for the super admin it exists
         for. */
      api.post<NotifyResultDto>(`/duties/${dutyId}/notify`, division ? { divisionOrgUnitId: division } : {}),
    onSuccess: (result) => void message.success(DUTY_FILL_COPY.notifySent(result.notified)),
    onError: (error: ApiError) => void message.error(error.message),
  });

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3c.2] COMPLETE WITH SHORT-FALL — THE ACT [C3c.1 §6a] REMOVED, ON A WIDER AUDIENCE.
   *
   *  A division asked for ten U/A men that can supply seven closes its share at seven and says
   *  why. `POST /duties/:id/completions` and `completeShares` are UNCHANGED — §6a's own note
   *  predicted that restoring this would be "a button and a modal against a contract that never
   *  moved", and it was. What is new is the capability behind the route and who may see it.
   *
   *  ⚠️ IT WRITES THE ANSWER STRAIGHT ONTO `sheetKey`, LIKE `saveStaged` AND UNLIKE
   *  `removeDelegation`. `completeShares` returns `getFillSheet(..., input.divisionOrgUnitId)`
   *  — the same narrowing this page asked for — so there is no wider-or-narrower sheet to
   *  clobber, which is the case `refreshSheet` exists for one mutation down.
   *
   *  ⚠️ AND IT `flushCounts()`, BECAUSE A CLOSED SHARE CHANGES A NUMBER TWO SCREENS AWAY. The
   *  inbox sorts on `fill_rank` and the bell counts outstanding work; both are derived from the
   *  share this write just closed. `setQueryData` neither refetches nor marks stale, so the hot
   *  path's guarantee is untouched — the flush is the ordinary on-the-way-out one.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const completeShortfall = useMutation({
    mutationFn: (reason: string) =>
      api.post<FillSheetDto>(`/duties/${dutyId}/completions`, {
        shortfallReason: reason,
        /* ⚠️ [D4.5 §3e] The SAME division the sheet was narrowed to, for `notify`'s reason
           verbatim: omitting it closes every share the caller can see, which is right for a
           nodal and wrong for the super admin the parameter exists for. */
        ...(division ? { divisionOrgUnitId: division } : {}),
      }),
    onSuccess: (fresh) => {
      queryClient.setQueryData<FillSheetDto>(sheetKey, fresh);
      /*
       * ⚠️ `markDirty()` FIRST, OR THE FLUSH BELOW IS A NO-OP — MEASURED.
       *
       * `flushCounts` opens with `if (!countsDirty.current) return;`, and that flag is set by
       * `markDirty` and by the assign/swap/remove handlers ALONE. On a visit whose only write is
       * a short-fall it is still false, so the invalidation never runs: a nodal records the
       * short-fall, presses back, and the inbox row this feature exists to clear is still red
       * for `staleTime`'s thirty seconds. `saveStaged.onSuccess` is the idiom being copied.
       */
      markDirty();
      flushCounts();
      void message.success(DUTY_FILL_COPY.shortfallDone);
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C2a §5e] REMOVE ONE UNIT FROM A SAVED SPLIT, WITHOUT TYPING A ZERO.
   *
   *  `POST /duties/:id/delegations` REPLACES a source row's whole split, so "remove this unit"
   *  is expressed as the same split MINUS that unit — the counts of every other target are
   *  re-sent verbatim, which is what stops a removal from silently re-planning the rest.
   *
   *  ⚠️ THE TARGETS COME FROM THE SHEET, NOT FROM `DelegateInline`'s FORM STATE. The form may
   *  never have been opened; the delegated rows are on screen either way and are what the
   *  operator is looking at. That is also why a removal cannot depend on the seeding fix
   *  [UX1 §2] added — it reads the saved split directly.
   *
   *  ⚠️ AND IT SHARES `onDelegated`'s SUCCESS PATH. `refreshSheet` rather than writing the
   *  response onto the key: `delegateAllocation` answers with a sheet narrowed to the SOURCE's
   *  division, which for a CP-office nodal viewing a two-division duty would make the other
   *  division's posts vanish ([C1f §1e]'s recorded defect).
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const removeDelegation = useMutation({
    mutationFn: ({
      lineKey,
      allocKey,
      unitOrgUnitId,
    }: {
      lineKey: string;
      allocKey: string;
      unitOrgUnitId: string;
    }) => {
      const line = lines.find((row) => row.lineKey === lineKey);
      const targets = (line?.allocations ?? []).flatMap((row) =>
        row.delegatedFromAllocKey === allocKey && row.unit && row.unit.id !== unitOrgUnitId
          ? [{ unitOrgUnitId: row.unit.id, count: row.allocatedCount }]
          : [],
      );
      return api.post<FillSheetDto>(`/duties/${dutyId}/delegations`, { lineKey, allocKey, targets });
    },
    onSuccess: (_sheet, { lineKey, allocKey }) => {
      void refreshSheet();
      void queryClient.invalidateQueries({ queryKey: queryKeys.fill.delegation(dutyId, lineKey, allocKey) });
      flushCounts();
      void message.success(DUTY_FILL_COPY.delegationRemoved);
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const copy = useMutation({
    mutationFn: () => api.get<CopyProposalDto>(`/duties/${dutyId}/copy-source`),
    onSuccess: (proposal) => {
      if (proposal.rows.length === 0) {
        void message.info(proposal.source ? DUTY_FILL_COPY.copyNothingToFill : DUTY_FILL_COPY.copyNothing);
        return;
      }
      /*
       * ⚠️ IT FILLS EMPTY SLOTS ONLY, AND COMMITS NOTHING (FR-5.3.4). A slot somebody named
       * while the operator was thinking is never overwritten — that would make "copy" a
       * destructive verb — and nothing reaches the server until Save.
       */
      const empty = new Set(
        lines.flatMap((line) =>
          line.allocations.flatMap((allocation) =>
            allocation.slots.filter((slot) => !slot.assignment?.officer).map((slot) => slot.slotRef),
          ),
        ),
      );
      const next = new Map<string, OfficerSummaryDto>();
      for (const row of proposal.rows) if (empty.has(row.slotRef)) next.set(row.slotRef, row.officer);
      if (next.size === 0) {
        void message.info(DUTY_FILL_COPY.copyNothingToFill);
        return;
      }
      setStaged(next);
      setCopiedFrom(proposal.source);
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  // ⚠️ Armed ONLY while a copy-from-last-time proposal is on screen. Individual selections
  // commit immediately, so there is nothing else on this page that can be lost.
  useUnsavedGuard({
    when: staged.size > 0,
    confirm: () =>
      new Promise<boolean>((resolve) => {
        modal.confirm({
          title: DUTY_FILL_COPY.unsavedGuard,
          okText: 'Leave',
          cancelText: 'Stay',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
  });

  const openAt = (lineKey: string, allocKey: string, slotRef: string, mode: 'fill' | 'swap'): void =>
    setCursor({ lineKey, allocKey, slotRef, mode });

  const onPick = (chosen: CandidateDto): void => {
    if (!cursor) return;

    if (cursor.mode === 'swap') {
      const assignment = lines
        .flatMap((line) => line.allocations.flatMap((allocation) => allocation.slots))
        .find((slot) => slot.slotRef === cursor.slotRef)?.assignment;
      if (assignment) {
        // ⚠️ Nudged only once the share is complete (FR-5.3.7); silent before that.
        if (data?.fillState === 'COMPLETE') {
          setSwapReasonFor({
            assignmentId: assignment.id,
            officerId: chosen.id,
            slotRef: cursor.slotRef,
            officer: chosen,
          });
        } else swap.mutate({ assignmentId: assignment.id, officerId: chosen.id });
        /*
         * ⚠️ [C1a §3] THE SLOT NOW HOLDS THE INCOMING OFFICER, AND THE OUTGOING ONE IS FREE.
         *
         * This branch used to `return` two lines below without touching the set at all, so
         * an officer named earlier in the sitting and then swapped out stayed greyed for the
         * life of the page — which is the reported defect. Keying on `slotRef` means one
         * write does both: it records B and evicts A.
         */
        rememberPick(cursor.slotRef, chosen);
      }
      setCursor(null);
      return;
    }

    /*
     * ⚠️ THE CURSOR ADVANCES BEFORE THE WRITE RESOLVES. Waiting for the response would put a
     * network round trip between two keystrokes twelve times over, which IS the budget.
     *
     * ⚠️ AND IT MUST REMEMBER EVERY PICK OF THE SITTING, NOT ONLY THIS ONE.
     *
     * `nextEmptySlot` WRAPS (`% sequence.length`) and reads emptiness from the CACHED sheet,
     * which only learns a slot is filled when that slot's response lands in `putSlot`. This
     * used to pass `staged + the current pick`, and `staged` is empty during ordinary filling
     * — so once the cursor reached the last slot it wrapped and found the earlier slots whose
     * writes were still in flight, and parked on one of them. The sheet then never reached
     * "no empty slot left" and never self-closed.
     *
     * ⚠️ INVISIBLE ON A FAST DATABASE, AND THAT IS WHY IT SURVIVED. Against a local Mongo each
     * response lands between two keystrokes and the cache is never stale; against Atlas over a
     * WAN it reproduces every time, parked on "slot 11 of 12" with all twelve rows ASSIGNED on
     * the server. Latency is the whole difference — a real operator on a slow connection meets
     * this, a developer on localhost never does.
     *
     * `inFlight` is a REF, not state: it must not schedule a render (the cursor change already
     * does), and it is read synchronously on the very next keystroke. It is only ever consulted
     * for CURSOR arithmetic — never for rendering — so a chip still shows as filled from the
     * server's answer rather than from a local guess.
     */
    assign.mutate({ slotRef: cursor.slotRef, officerId: chosen.id });
    // ⚠️ [PRE-P6 §2b] The mirror is written through `rememberPick` and nowhere else.
    rememberPick(cursor.slotRef, chosen);
    const optimistic = new Map([...inFlight.current, ...staged]);

    const next = nextEmptySlot(lines, cursor, optimistic);
    if (next) {
      setCursor(next);
      return;
    }

    /*
     * ─────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [PRE-P6.2] THE ROW IS FULL, SO THE SHEET CLOSES ITSELF — AND SAYS WHY, ONCE.
     *
     *  [PRE-P6] §2c held the cursor and rendered a completion panel with a Close button.
     *  That is one click for something the operator has no other answer to, and the sheet
     *  covers the very row they are being asked to look at next. It closes now, and the
     *  reason moves to a message at the foot of the screen.
     *
     *  ⚠️ AND NOT WHEN THE WHOLE SHEET IS DONE. "Every slot on this post is named, pick
     *  another post" is wrong when there is no other post to pick: the footer already turns
     *  green, the share auto-completes server-side, and a message telling somebody to carry
     *  on is exactly the false done-signal §2c set out to remove. The last row closes
     *  silently, which is P4's own self-close and needs no addition.
     *
     *  ⚠️ THE COUNT IS OPTIMISTIC, deliberately: it reads `inFlight` and `staged` like the
     *  cursor does, so the message is decided on what the operator has DONE rather than on
     *  what has come back. Waiting for the writes would put the toast a round trip late, on
     *  the connection this screen exists for.
     */
    setCursor(null);
    if (countNamed(lines, optimistic) < countSlots(lines)) notifyRowFilled();
  };

  if (!officer) return null;

  const total = countSlots(lines);
  const named = countNamed(lines, staged);
  /*
   * ⚠️ [PRE-P6 §2b] WHO IS ALREADY ON THIS DUTY, ANSWERED LOCALLY AND INSTANTLY.
   *
   * Three sources, all already on this page: the cached sheet (what the server has told us),
   * `staged` (a copy proposal nobody has saved) and `picked` (this sitting's writes, some
   * still in flight). `namedOfficerIds` is shared with the detail page's fill surface — see
   * its header for why that is one function rather than two.
   *
   * ⚠️ IT ADDS NO QUERY AND NO KEY, so there is nothing new for an invalidation to reach —
   * `lib/fillKeyIsolation.test.ts` reads every production file for exactly that.
   */
  const alreadyNamed = namedOfficerIds(lines, staged.values(), picked.values());
  const position = cursor ? slotPosition(lines, cursor) : null;
  const isComplete = data?.fillState === 'COMPLETE';
  /*
   * ⚠️ [C5b §3] THE FOOTER'S WORD AND TONE ARE THE SHEET'S, INCLUDING `closedShort`. It printed
   * `FILL_STATE_LABEL.COMPLETE` for every COMPLETE sheet, so a division that closed short at
   * nobody named read `🔒 All Filled · 0 of 12 named` (S07). The count stays the CLIENT's (staged
   * names in, vans out) — `fillFooter` takes both halves and says which is which.
   */
  const footer = fillFooter(
    {
      fillState: data?.fillState ?? 'NOT_NAMED',
      closedShort: data?.closedShort ?? false,
      allocatedCount: data?.allocatedCount ?? 0,
    },
    named,
    total,
  );

  /*
   * ⚠️ [C3c §3d] THREE INPUTS, FIVE VERBS, ONE NOTICE — and never a single `readOnly`.
   *
   * `readOnly` above is the CAPABILITY alone. The two windows close at different instants, so
   * this screen has a state in which naming is refused and a swap is still offered; a boolean
   * cannot express it, and collapsing the three is what `DutyOfficers` used to do wrong.
   *
   * ⚠️ `?? false` WHILE LOADING, in both. A window assumed OPEN would render every control for
   * a frame and then withdraw it, which is worse than the reverse: an operator who clicks in
   * that frame gets a 409 for a duty that was never fillable.
   */
  const controls = fillControlsFor({
    canWrite: !readOnly,
    /*
     * ⚠️ [C3c.2] A SECOND CAPABILITY, NOT A CONSEQUENCE OF THE FIRST. `readOnly` above is
     * `arrangement.write`; this is `fulfilment.completeShortfall` plus the seat rule, and the
     * two grant lists genuinely differ — a NODAL_MONITOR holds the second and not the first,
     * which is the whole of what the client asked for.
     */
    canCompleteShortfall: mayCompleteShortfall(officer?.accessRole ?? 'NONE', actingOrgUnitTypeOf(officer)),
    namingOpen: data?.namingWindowOpen ?? false,
    swapOpen: data?.swapWindowOpen ?? false,
  });

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3c.2] THE ADDRESSEES THAT HAVE CLOSED SHORT — the client's half of
   *  `notifyNamedOfficers`' `excused` set, read off the same DTO the server wrote it from.
   *
   *  ⚠️ `shortfallReason !== null` AND NOT `completedAt !== null`. `autoCompleteFullShares`
   *  closes a share the instant its last slot is named and writes NO reason, so completion
   *  alone is true of the ordinary full case too. `completeShares` is the sole writer of that
   *  column and its schema requires it, so this is exactly the set somebody closed short.
   *
   *  ⚠️ AND THE ADDRESSEE IS `orgUnit`, NEVER `division`. Completion is per ADDRESSEE per duty
   *  (FR-5.3.6b), and `addresseeOf` makes that the UNIT wherever a line names one — reading the
   *  division here would excuse every station under it from one station's short-fall.
   *
   *  ⚠️ [P7A §4] ONE DERIVATION FOR TWO READERS — `closedShortReasonsOf`. The rows' closed-short line
   *  (`FillLines`) states the same set this excuses from Send Notification, with the division's own
   *  words, so the screen cannot excuse an addressee it does not say has replied, or the reverse.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const closedShortReasons = closedShortReasonsOf(data?.shares ?? []);
  const excusedAddressees = new Set(closedShortReasons.keys());
  /*
   * ⚠️ HOW MANY SHARES THE CLICK WOULD CLOSE — the button's own enabled state AND the modal's
   * opening sentence, from one derivation.
   *
   * There is nothing to close once every visible share is complete: that is the 409
   * `completeShares` answers, tested before the click rather than after it. And the COUNT
   * matters as much as the zero — `completeShares` closes every open share it can see when no
   * `?division=` narrows the sheet, so on a super admin's unnarrowed view "your share" would
   * be a sentence about one row and an act on four.
   */
  const openShares = (data?.shares ?? []).filter((share) => share.completedAt === null);
  const hasOpenShare = openShares.length > 0;

  /*
   * ⚠️ [C3c.1 §6b] "EVERYONE IS NAMED", COUNTED THE WAY THE SCREEN COUNTS. `total` and `named`
   * both come from `slotSequence`, which excludes vehicles — so this is the same set the server
   * checks and the button cannot offer a send the API refuses.
   *
   * ⚠️ [amended C3c.2] AND "EVERYONE" NOW MEANS "EVERYONE STILL BEING ASKED FOR". A share closed
   * with a short-fall stops asking for its empty slots, so `named < total` would keep the send
   * refused for ever on exactly the duty the short-fall was recorded to unblock. `countOutstanding`
   * is the same funnel with the excused addressees removed.
   *
   * ⚠️ THE `named === 0` TERM IS NOT REDUNDANT. It is unreachable while nothing is excused —
   * an empty slot is an outstanding one — and it is the whole of the fully-excused case, where
   * a send would spend the hour's rate slot and tell nobody. The server refuses it too.
   */
  const outstanding = countOutstanding(lines, staged, excusedAddressees);
  const notReady = total === 0 || named === 0 || outstanding > 0;

  /*
   * ⚠️ THREE REASONS, THREE SENTENCES — [C3c §3c]'s rule. A capability refusal shown for an
   * unfilled slot tells a nodal something false about their own access.
   *
   * ⚠️ [amended C3c.2] AND THE CAPABILITY ARM GOES THROUGH `refusalFor`, WHICH IS THE WHOLE
   * POINT OF THE FIFTH NOTICE. It read `READ_ONLY_TOOLTIP` directly — the one string this pass
   * minted `SHORTFALL_ONLY_TOOLTIP` to stop showing a monitor — so the role the feature was
   * built for met a live `Complete With Short-Fall` button and, on the control immediately
   * beside it, a tooltip saying their access changes nothing. Every other control on this page
   * already routed through the resolver; this one did not, and the new suite hovered an `Add`
   * button rather than this one, so nothing caught it.
   */
  const notifyRefusal = !controls.mayNotify
    ? refusalFor(controls)
    : total === 0
      ? DUTY_FILL_COPY.notifyNothingAsked
      : outstanding > 0
        ? DUTY_FILL_COPY.notifyNotFull
        : named === 0
          ? DUTY_FILL_COPY.notifyNobodyNamed
          : undefined;
  const hasStaged = staged.size > 0;
  /*
   * ⚠️ [C3c §5c] THE ADDRESSEE'S OWN OBLIGATION, AND IT CANNOT LEAK UPWARD — `sheetDischarged`
   * returns false for any `oversight` reader, which is every CP-office node and every super
   * admin. See its header for the three independent facts that keep that true.
   */
  /*
   * ⚠️ HOISTED SO THE ROSTER AND THE FOOTER SHARE ONE DERIVATION. It was inline at the
   * `FillLines` call site; §5c gave it a second reader, and two calls is two places for the
   * same person to be told two different things about one sheet.
   */
  const audience = fillAudienceFor(officer?.accessRole ?? 'NONE', actingOrgUnitTypeOf(officer));
  /*
   * ⚠️ [C6a §4] THE DIVISION'S COMMAND HALF, AS TWO BOOLEANS — REQUIRED BY `sheetDischarged`, never
   * defaulted, so a position still addressed to the division holds "Your part is done" shut however
   * full its force posts are, and a division asked only for command can be done at all.
   */
  const discharged = sheetDischarged(lines, new Set(staged.keys()), audience, commandObligation(commandFamilies));
  /*
   * ⚠️ [C6a §4] EVERY AWAITED ROLE, IN WORDS, FOR THE FOOTER — and shown on a LOCKED force share too,
   * which is the whole reason it is not folded into the pill: `autoCompleteFullShares` locks the force
   * the moment its last slot is named, and `🔒 All Filled` over an unnamed In-charge reads as done.
   *
   * ⚠️ BUT ONLY WHILE THIS DUTY'S NAMING WINDOW IS OPEN — the server's own per-duty rule. The printed
   * order and the officer app take their "awaited" from `awaitedCommandsOnDuty`, which answers nothing
   * once no name can reach the duty ("a promise nobody can keep"), and `controls.mayName` is false then,
   * so nothing on this screen could act on it. `commandObligation` stays UNGATED: the pill stays shut
   * over an unnamed position after the window, exactly as the force half's does over an empty slot.
   */
  const awaited = data?.namingWindowOpen ? awaitedCommands(commandFamilies, audience) : [];
  /*
   * ⚠️ [C6a §4] A COMMAND-ONLY SHEET — a division asked for an In-charge and no force. It has no force
   * share, so every force control in the footer would describe nothing: a pill reading `0 of 0 named`,
   * a short-fall with nothing open and a notification with nobody to tell. They are not rendered, and
   * neither is the empty state, which would say nothing here is addressed to a division that is being
   * asked for a commander.
   */
  const commandOnly = commandFamilies.length > 0 && lines.length === 0 && (data?.shares.length ?? 0) === 0;
  /*
   * ⚠️ [P7A §3] A VEHICLE-ONLY SHEET — an addressee asked for a van and no person. A van is in no share
   * (it is counted, never named), so the sheet carries its vehicle lines read-only and nothing to fill:
   * the force pill would read `0 of 0 named`, `Complete with short-fall` would close a vehicle short —
   * which the client's rule says is never shown — and Notify would have nobody to tell. The same three
   * sites `commandOnly` gates; the lines render, so no empty state appears.
   */
  const vehicleOnly = (data?.shares.length ?? 0) === 0 && lines.length > 0 && lines.every((line) => !isStaffable(line));
  const noForceToFill = commandOnly || vehicleOnly;

  /*
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️⚠️ [C3c.2] NOTHING IS RENDERED UNTIL THE SHEET HAS RESOLVED — AND THE BUG THIS FIXES IS
   *  THE `?? false` TWENTY LINES ABOVE, WHICH IS CORRECT AND WAS NEVER THE WHOLE ANSWER.
   *
   *  Those two defaults exist so a window is never assumed OPEN while loading — an operator who
   *  clicks in that frame would get a 409 for a duty that was never fillable, and that reasoning
   *  stands. What it did not account for is that `fillControlsFor` reads BOTH as closed and
   *  therefore answers `notice: 'allClosed'`, so the page opened on **"This duty is over"** for
   *  every duty, including one running right now, and then swapped it out when the data landed.
   *  A banner that says the opposite of the truth for a frame is worse than a blank one: it is
   *  read, believed, and acted on.
   *
   *  ⚠️ THE GATE IS `sheet.isPending`, NOT `data === undefined`. They differ on the case that
   *  matters: a FAILED sheet also has no data, and gating on the data would spin a skeleton for
   *  ever over an error the page already handles. `isPending` is true only while the FIRST fetch
   *  is in flight, so an error falls through to exactly the render it reaches today.
   *
   *  ⚠️ IT REMOVES NO DATA POINT AND CHANGES NO ARM. Every hook on this page runs above this
   *  line — `react-hooks` would refuse otherwise — and once the sheet resolves the tree below is
   *  byte-identical to what it has always been. This adds a state that did not exist; it edits
   *  none that did.
   *
   *  ⚠️ AND IT IS A SKELETON RATHER THAN A SPINNER, which is this repository's own rule for a
   *  slow surface: `components/Skeleton.tsx` keeps `role="status"` and an `sr-only` sentence, so
   *  what a screen reader is told does not depend on which branch rendered.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  if (sheet.isPending) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-4" data-testid="fill-loading">
        <Loading label={DUTY_FILL_COPY.loading}>
          <Skeleton className="h-7 w-80" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </Loading>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4" data-testid="fill-root">
      <PageHeader
        className="shrink-0"
        icon={ClipboardCheck}
        /*
         * ⚠️ [D4.3] `/inbox`, NOT the order. A nodal arrives here from an inbox row holding a
         * duty id and not the order's (`routes/index.tsx` says so), so the inbox is the one
         * destination that is right however this page was reached — which is exactly the
         * property `history.back()` does not have.
         */
        parent={{ label: 'Assign PA', to: '/inbox' }}
        title={DUTY_FILL_COPY.title}
        /*
         * ─────────────────────────────────────────────────────────────────────────────────
         *  ⚠️ [amended C2a §5b] THE IDENTITY LINE IS BACK — REVERSING [PRE-P6.2], ON INSTRUCTION.
         *
         *  That pass removed it on instruction too, and recorded the cost precisely: it was
         *  "the only thing on the screen that said WHICH duty was being filled". The client has
         *  asked for the PA name and the order number back, which is that cost being paid back.
         *  Recorded as a reversal rather than as a new feature, so the next reader finds both
         *  decisions and not just the latest one.
         *
         *  ⚠️ THE OPERATOR'S OWN `paNumber` LEADS, WITH `reference` AS THE FALLBACK. `paNumber`
         *  is what is written on the paper order and is what somebody rings up about; the
         *  system's `PA-2026-…` reference is what we can always produce. It is `null` on plenty
         *  of orders, which is exactly why it cannot be the only thing shown.
         *
         *  ⚠️ AND IT ADDS NO REQUEST. Every field is already on `fillSheet.duty` — this screen
         *  has been fetching them and rendering none of them.
         *
         *  ⚠️ [P7A §2] AND THE ORDER'S RAISER PILL ENDS THE LINE — `HQ` · `DRO` · `Unit`, beside the
         *  duty point the line names. `fillSheet.duty.raisedAtLevel` carries it; `null` adds nothing,
         *  not even the separating space, so such a sheet's identity line is what it always was.
         * ─────────────────────────────────────────────────────────────────────────────────
         */
        subtitle={
          data ? (
            <>
              {DUTY_FILL_COPY.identity(data.duty)}
              {data.duty.raisedAtLevel === null ? null : (
                <>
                  {' '}
                  <RaiserPill level={data.duty.raisedAtLevel} />
                </>
              )}
            </>
          ) : undefined
        }
        actions={
          <>
            {/* ⚠️ [PRE-P6.2] READ-ONLY, SO IT IS OFFERED TO EVERY ROLE. A history is the one
                thing on this screen a NODAL_READ has as much business seeing as a nodal who
                can write — and `arrangement.read` is what the endpoint asks for. */}
            <Tooltip title={FILL_COPY.historyTrigger}>
              <Button
                icon={<History className="size-4" aria-hidden />}
                aria-label={FILL_COPY.historyTrigger}
                data-testid="duty-history-open"
                onClick={() => setHistoryOpen(true)}
              />
            </Tooltip>
            {/* ⚠️ [C3c §3a] A COPY PROPOSAL IS A NAMING ACT, so it closes with the naming window
                rather than with the capability alone. The sentence explains which.
                ⚠️ [C6a review] AND IT IS A FORCE VERB, so a command-only sheet does not offer it —
                the footer's pill and actions already go there, and this one only ever answered
                "nothing to fill". A command position is never copied from last time. */}
            {noForceToFill ? null : (
              <Tooltip title={refusalFor(controls, 'name')}>
                <Button disabled={!controls.mayName} loading={copy.isPending} onClick={() => copy.mutate()}>
                  {DUTY_FILL_COPY.copyFromLast}
                </Button>
              </Tooltip>
            )}
          </>
        }
      />

      {/*
        THE ONE SCROLL CONTAINER ON THIS PAGE. `min-h-0` is what lets a flex child shrink
        below its content and actually scroll.

        ⚠️ [UX1.1 §3] `pr-3` IS THE SCROLLBAR'S GUTTER, AND IT IS ON THE SCROLLER RATHER THAN ON
        THE ROWS. Every post's count is right-aligned to the row's own edge — [C1h §3a], which
        `fillGaps.spec.ts` measures as flush right — so a classic overlay scrollbar lands on top
        of "12 of 12 named" for as long as the operator is scrolling, which is precisely when
        they are reading it.

        ⚠️ PADDING THE SCROLLER MOVES BOTH EDGES TOGETHER. The count is measured against
        `fill-post`'s own right edge, and both sit inside this box — so the flush-right claim is
        unaffected, where padding the ROWS would have moved one and not the other.

        ⚠️ AND IT IS UNCONDITIONAL, not a `scrollbar-gutter` query. Overlay scrollbars make it
        3px of harmless breathing room; classic ones make it the difference between a legible
        number and a covered one. There is no width to branch on that jsdom could see anyway.
      */}
      {/*
        ⚠️ [C3c §3d] A CLOSED PAGE IS VISIBLY CLOSED — not a page whose saves silently fail.
        ⚠️ ONE BANNER, CHOSEN BY `notice`, NEVER TWO STACKED. The three refusals are mutually
        exclusive by construction (`fillControlsFor` returns exactly one), and a wall of banners
        is a screen nobody reads. `noCapability` renders none: a role without `arrangement.write`
        already meets a read-only app everywhere and needs no sentence on this screen in
        particular — which is the distinction `DutyOfficers` has always drawn.
      */}
      {/* ⚠️ [amended C3c.1] `swapClosed` JOINS THEM — see `FILL_NOTICES`. A 06:00–06:30 duty point
          closes SWAPPING before naming, and that arm used to render no banner and no tooltip at
          all: Swap vanished from every `⋮` with nothing on screen saying why. */}
      {controls.notice === 'namingClosed' || controls.notice === 'swapClosed' || controls.notice === 'allClosed' ? (
        <Alert
          className="shrink-0"
          type="info"
          showIcon
          icon={<Lock className="size-4" aria-hidden />}
          data-testid={WINDOW_BANNER_TESTID[controls.notice]}
          message={refusalFor(controls)}
        />
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto pr-3" data-testid="fill-scroll">
        {/*
          ⚠️ [C6a §4] ABOVE THE FORCE GRID, AND NEVER IN IT. Its handles are its own (`command-family-*`,
          `command-seat-*`), because `fillGaps.spec.ts` reaches the grid through `fill-line`,
          `fill-chips`, `slot-add` and `fill-post` with `.first()` — a command row wearing one would be
          measured in its place.

          ⚠️ A SEAT WRITE'S ANSWER IS WRITTEN BACK WITH `setQueryData`, NEVER AN INVALIDATION. Every verb
          answers with the caller's families on this duty, narrowed by this page's own `?division=`, and
          nothing else on the sheet can have moved — no force line, share or slot is touched by them — so
          re-reading the whole sheet would be a force read for a command answer (`commandSeat.schema.ts`
          says the same of the server). `markDirty` so the one flush on the way out refreshes the order's
          record, which states who each division named. A REFUSED write is the one that re-reads — it has
          no answer to write, and the refusal says the cached families are stale.
        */}
        {commandFamilies.length > 0 ? (
          <CommandFamilies
            dutyId={dutyId}
            division={division}
            families={commandFamilies}
            controls={controls}
            namingOpen={data?.namingWindowOpen ?? false}
            onChanged={(families) => {
              queryClient.setQueryData<FillSheetDto>(sheetKey, (current) => withCommandFamilies(current, families));
              markDirty();
            }}
            /* ⚠️ A refusal carries no families, so the one re-read replaces the stale ones — `swap` and
               `remove`'s own error path in `useFillSheet`, and a `setQueryData`, never an invalidation. */
            onRefused={() => void refreshSheet()}
          />
        ) : null}

        {hasStaged && copiedFrom ? (
          <Alert
            className="mb-3"
            type="info"
            showIcon
            message={DUTY_FILL_COPY.copyingFrom(copiedFrom.reference, copiedFrom.dutyDate, staged.size)}
          />
        ) : null}

        {/*
          ⚠️ [C5c §2h] A DUTY WHOSE ONLY FORCE IS SHARED FROM ANOTHER DUTY POINT HAS NO LINES AND IS
          NOT EMPTY — somebody covers it. The empty state is for a duty that asks for nobody.
        */}
        {/* ⚠️ [C6a §4] …and a duty whose only ask of this division is a command role is not empty either. */}
        {lines.length === 0 && sharedLines.length === 0 && commandFamilies.length === 0 && !sheet.isFetching ? (
          <EmptyState title={DUTY_FILL_COPY.empty} size="md" />
        ) : (
          <FillLines
            lines={lines}
            staged={staged}
            cursor={cursor}
            controls={controls}
            /*
             * ⚠️ [UX1 §3c] THE VIEWER'S OWN QUESTION, NOT THE ROUTE'S. `?division=` narrows
             * WHICH lines this sheet shows; it says nothing about who is reading them, and a
             * CP-office nodal drilled into one division is still oversight. One derivation,
             * shared with `ArrangementDetail`'s duty panel, so the same person cannot see one
             * post green here and amber two clicks away.
             */
            audience={audience}
            /* ⚠️ [P7A §4] Which addressees replied short, and why — the excused set's own derivation. */
            closedShortReasons={closedShortReasons}
            onAdd={(lineKey, allocKey, slotRef) => openAt(lineKey, allocKey, slotRef, 'fill')}
            onSwap={(lineKey, allocKey, slotRef) => openAt(lineKey, allocKey, slotRef, 'swap')}
            onRemove={(slotRef, assignmentId) => {
              if (staged.has(slotRef)) {
                // A staged chip is local. Removing it is not a request.
                const next = new Map(staged);
                next.delete(slotRef);
                setStaged(next);
                return;
              }
              if (assignmentId) {
                remove.mutate({ slotRef, assignmentId });
                /* ⚠️ [C1a §3] The slot is empty again and the officer is free. Leaving them
                   in the set greyed somebody the operator had just deliberately released. */
                rememberPick(slotRef, undefined);
              }
            }}
            postMode={postMode}
            /*
             * ⚠️ [C1b §3 / amended C1f §1c] READ-ONLY GETS NO CONTROL AT ALL, AND NEITHER DOES
             * A UNIT NODAL. The server refuses a read-only caller either way
             * (`dashboardStack('arrangement.write')`), so that half is about not OFFERING an
             * act; the unit half is an OFFER gate over a verb the server ALLOWS — a unit with
             * nothing beneath it would open onto an empty picker. `mayDelegateAllocation`'s
             * header says why it is not shared with `mayAuthorArrangement`.
             */
            onPostMode={
              !controls.mayDelegate || !mayDelegateAllocation(actingOrgUnitTypeOf(officer))
                ? undefined
                : (lineKey, allocKey, mode) => {
                    setPostMode((current) => new Map([...current, [`${lineKey}|${allocKey}`, mode]]));
                  }
            }
            onDelegationRemove={
              !controls.mayDelegate || !mayDelegateAllocation(actingOrgUnitTypeOf(officer))
                ? undefined
                : (lineKey, allocKey, unitOrgUnitId) => {
                    modal.confirm({
                      title: DUTY_FILL_COPY.removeDelegationTitle,
                      content: DUTY_FILL_COPY.removeDelegationIntro,
                      okText: DUTY_FILL_COPY.removeDelegationConfirm,
                      okButtonProps: { danger: true },
                      onOk: () => {
                        removeDelegation.mutate({ lineKey, allocKey, unitOrgUnitId });
                      },
                    });
                  }
            }
            renderDelegate={(lineKey, allocKey) => (
              <DelegateInline
                dutyId={dutyId}
                lineKey={lineKey}
                allocKey={allocKey}
                onCancel={() => {
                  setPostMode((current) => new Map([...current, [`${lineKey}|${allocKey}`, 'name']]));
                }}
                /*
                 * ⚠️ [amended C1f §1e] ONE RE-READ, NOT A CACHE WRITE OF THE ANSWER.
                 *
                 * [C1b] wrote the response straight onto `sheetKey`, and that carried a latent
                 * narrowing bug: `delegateAllocation` returns `getFillSheet(id, scope, cameFrom)`
                 * where `cameFrom` is the SOURCE's division, so for a CP-office nodal viewing a
                 * two-division duty, splitting CD's row wrote a CD-only sheet over the
                 * un-narrowed key and ND's posts vanished from the screen. It read as a repaint
                 * before §1e; it reads as half the sheet disappearing after it.
                 *
                 * `refreshSheet` carries the page's own `division`, and it is a `setQueryData`
                 * at a boundary that happens ONCE — [PRE-P6.2]'s rule, not an invalidation, so
                 * [P4]'s hot path is intact and `fillKeyIsolation.test.ts` is satisfied by
                 * construction. A delegation changes the sheet's SHAPE, so a per-slot patch was
                 * never the right instrument.
                 */
                onDelegated={() => {
                  setPostMode((current) => new Map([...current, [`${lineKey}|${allocKey}`, 'name']]));
                  void refreshSheet();
                  /*
                   * ⚠️ [UX1 §2] THE OPTIONS THE CONTROL SEEDS FROM, RE-READ.
                   *
                   * §2 lets the control re-open on an already-split row, and `DelegateInline`
                   * now seeds its form from `options[].delegatedCount`. Without this the cached
                   * answer is a release behind, so a second open would seed the PREVIOUS split
                   * and confirming it would undo the one just made — the same data loss the
                   * seeding fix exists to prevent, arriving one step later.
                   *
                   * ⚠️ IT IS PERMITTED BY `fillKeyIsolation.test.ts`, which bans an invalidation
                   * at or above a live SHEET (`fill.all`, `fill.sheets`, `fill.sheet`).
                   * `fill.delegation` is a declared SIBLING, so this reaches no sheet.
                   */
                  void queryClient.invalidateQueries({
                    queryKey: queryKeys.fill.delegation(dutyId, lineKey, allocKey),
                  });
                  flushCounts();
                }}
              />
            )}
          />
        )}

        {sharedLines.length > 0 ? <SharedLines lines={sharedLines} division={division} /> : null}

        {data && data.dropped.length > 0 ? (
          <section className="mt-6 rounded-md border border-border p-3" data-testid="dropped-strip">
            <h2 className="text-sm font-medium">{DUTY_FILL_COPY.droppedHeading}</h2>
            <p className="mb-2 text-xs text-muted-foreground">{DUTY_FILL_COPY.droppedIntro}</p>
            <ul className="flex flex-col gap-1 text-sm">
              {data.dropped.map((row) => (
                <li key={row.id}>
                  {row.officer?.name ?? '—'} <span className="text-muted-foreground">· {row.lineLabel}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      {/* A REAL FOOTER, A SIBLING OF THE SCROLLER — never `sticky bottom-0`. The negative
          margins pull it out to <main>'s padding edge so the rule spans the full width and
          nothing is left underneath it. */}
      <div
        data-testid="fill-footer"
        className="-mx-4 -mb-6 flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-background px-4 py-3 sm:-mx-6 sm:px-6"
      >
        <div className="flex flex-wrap items-center gap-2" aria-live="polite">
          {/* ⚠️ [C6a §4] The FORCE pill and count — absent on a command-only sheet, where `0 of 0 named`
              would be a statement about force nobody asked for. */}
          {noForceToFill ? null : (
            <>
              <StatusPill tone={footer.tone}>
                <span className="inline-flex items-center gap-1">
                  {footer.locked ? <Lock className="size-3.5" aria-hidden /> : null}
                  {footer.word ?? footer.count}
                </span>
              </StatusPill>
              {footer.locked ? <span className="text-sm text-muted-foreground">{footer.count}</span> : null}
            </>
          )}
          {/*
            ⚠️ [C3c §5c] A SECOND, ADJACENT PILL — NEVER A RECOLOURING OF THE FIRST.

            The pill beside it is the SHARE's state: the thing the server stored, the thing that
            COMPLETES (FR-5.3.6b), and the thing the inbox sorts on. Recolouring it from a client
            derivation would make this screen and the inbox disagree about one row, which is
            exactly what `postFillStateOf`'s header exists to prevent.

            Two pills say two TRUE things: "8 of 12 named" is the requirement, and this is the
            OBLIGATION — [UX1 §3]'s addressee reading, that handing capacity down to a station is
            a disposal of it. A division that has named some and delegated the rest has finished
            its part and the duty is still not fully staffed; both facts belong on screen.

            ⚠️ `!isComplete`, because once the share is locked the pill beside this one already
            says so and a second green claim is a wall of badges.
          */}
          {discharged && !isComplete ? (
            <StatusPill tone="success" data-testid="fill-discharged">
              {DUTY_FILL_COPY.discharged}
            </StatusPill>
          ) : null}
          {/*
            ⚠️ [C6a §4] AN AWAITED COMMAND ROLE, IN WORDS — NEVER GATED ON `isComplete` (only on the naming
            window, where `awaited` is derived above).

            The discharged pill hides once the force share locks, because the pill beside it already
            says so; that is exactly why this cannot be folded into it. A locked force with an unnamed
            In-charge is not done, and the words are what say so. One entry per role and addressee —
            "In-charge still to be named", "In-charge awaited from CD · TALA" — and no number: a
            command position is never counted (FR-5.2.6b). Warning-toned text, not a pill, so it adds
            no second fill-state badge to a footer whose colour means the force.
          */}
          {awaited.length > 0 ? (
            <ul
              className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-warning"
              data-testid="fill-command-awaited"
            >
              {awaited.map((entry) => (
                <li key={`${entry.label}|${entry.from ?? ''}`}>
                  {entry.from === null
                    ? DUTY_FILL_COPY.commandStillToName(entry.label)
                    : DUTY_FILL_COPY.commandAwaitedFrom(entry.label, entry.from)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {hasStaged ? (
            <>
              <Button
                onClick={() => {
                  setStaged(new Map());
                  setCopiedFrom(null);
                }}
              >
                {DUTY_FILL_COPY.discard}
              </Button>
              {/*
                ⚠️ [amended C3c.1] GATED ON `controls.mayName` — AND §3 SHIPPED IT UNGATED.

                Every other naming control on this screen reads `controls`; this one, the PRIMARY
                one, carried no `disabled` at all. The reachable case is not exotic: a nodal stages
                names while naming is open, a write elsewhere on the page calls `refreshSheet()`,
                the cache is replaced with a sheet whose `namingWindowOpen` is now false — and the
                Save button stays enabled and POSTs into a window the server refuses.

                ⚠️ THE CHIPS STAY VISIBLE UNDER THE BANNER. CLAUDE.md §8 [C3c §3c]: "A WINDOW CAN
                CLOSE WHILE STAGED NAMES ARE HELD. Save is disabled and the chips stay visible —
                dropping them silently is the one outcome an operator cannot recover from."
              */}
              <Tooltip title={refusalFor(controls, 'name')}>
                <Button
                  type="primary"
                  data-testid="save-staged"
                  disabled={!controls.mayName}
                  loading={saveStaged.isPending}
                  onClick={() => saveStaged.mutate()}
                >
                  {DUTY_FILL_COPY.saveStaged(staged.size)}
                </Button>
              </Tooltip>
            </>
          ) : noForceToFill ? /* ⚠️ [C6a §4] No force share: nothing to close short and nobody to tell. */ null : (
            /*
             * ⚠️ [C3c.1 §6a/§6b] `Send Notification`, WHERE `Mark complete` USED TO BE.
             *
             * The old control was `disabled` at exactly the state it described — FR-5.3.6
             * auto-completes at full — so §6a hides it and §6b puts a live verb in the slot.
             *
             * ⚠️ THE REFUSAL NAMES ITS OWN REASON. A capability refusal, an unfilled slot and a
             * sheet asking for nothing are three different sentences, and [C3c §3c]'s rule is
             * that one collapsed message tells an operator something false about themselves.
             */
            <>
              {/*
                ⚠️ [C3c.2] `Complete With Short-Fall`, BESIDE `Send Notification` AND NOT IN PLACE
                OF IT. The two are one sequence: closing short is what makes the send legal on a
                duty that will never be fully named, so the order on screen is the order of the
                acts.

                ⚠️ IT IS RENDERED AT ALL ONLY FOR A HOLDER OF THE CAPABILITY **AND** THE SEAT —
                `controls.mayCompleteShortfall`, which is `mayCompleteShortfall`'s answer threaded
                through the one policy table. Hidden rather than disabled, because a disabled
                control invites "why?" from somebody for whom there is no answer but their role.

                ⚠️ AND IT IS `disabled` RATHER THAN HIDDEN WHEN THERE IS NOTHING OPEN, which is the
                other half of that distinction: that IS a fact about the record, it has a sentence,
                and the sentence is the 409 the server would otherwise answer.
              */}
              {/*
                ⚠️ [COPY-REPEATS] COPY TO OTHER DATES, FIRST OF THE THREE — the order of the acts again: name
                this duty, copy it to the duty point's other dates, close short what cannot be filled, tell
                the officers. The primary stays rightmost.

                ⚠️ IT IS HANDED THE FOOTER'S OWN `named` AND `total`, the two numbers `fillFooter` is given
                above, so its 80% is the figure the operator is looking at and no second count exists. It
                sits in this branch, so a staged copy-from-last-time proposal — which `named` counts and the
                server does not hold — can never lift a duty over the line.

                ⚠️ NEVER DISABLED, for any role: a caller who cannot name by hand is told so in its modal,
                with this screen's own capability sentence — `refusalFor`'s, never a window's, because a
                closed naming window HERE says nothing about the other dates the copy writes.
              */}
              <CopyToOtherDates
                dutyId={dutyId}
                division={division}
                named={named}
                total={total}
                refusal={readOnly ? (refusalFor(controls) ?? READ_ONLY_TOOLTIP) : undefined}
                dutyPointTitle={data?.duty.dutyPointTitle ?? DUTY_FILL_COPY.copyRepeatsThisDutyPoint}
                shares={data?.shares ?? []}
                onCopied={() => {
                  /* `completeShortfall`'s idiom: `markDirty` first, or the flush is a no-op. */
                  markDirty();
                  flushCounts();
                }}
              />
              {controls.mayCompleteShortfall ? (
                <Tooltip title={hasOpenShare ? undefined : DUTY_FILL_COPY.shortfallNothingOpen}>
                  <Button
                    data-testid="complete-shortfall"
                    disabled={!hasOpenShare}
                    loading={completeShortfall.isPending}
                    onClick={() => {
                      setShortfallReason('');
                      setShortfallOpen(true);
                    }}
                  >
                    {DUTY_FILL_COPY.completeShortfall}
                  </Button>
                </Tooltip>
              ) : null}
              <Tooltip title={notifyRefusal}>
                <Button
                  type="primary"
                  data-testid="send-notification"
                  disabled={!controls.mayNotify || notReady}
                  loading={notify.isPending}
                  onClick={() => notify.mutate()}
                >
                  {DUTY_FILL_COPY.sendNotification}
                </Button>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <DutyHistoryDrawer dutyId={dutyId} open={historyOpen} onClose={() => setHistoryOpen(false)} />

      <OfficerSearchSheet
        open={cursor !== null}
        dutyId={dutyId}
        slotRef={cursor?.slotRef ?? ''}
        line={position?.line ?? null}
        slotIndex={position?.index ?? 0}
        slotTotal={position?.total ?? 0}
        scopeLabel={position?.allocationLabel ?? ''}
        mode={cursor?.mode ?? 'fill'}
        namedOfficerIds={alreadyNamed}
        onSelect={onPick}
        onClose={() => setCursor(null)}
      />

      <Modal
        open={swapReasonFor !== null}
        title={DUTY_FILL_COPY.swapTitle}
        okText={DUTY_FILL_COPY.swapConfirm}
        confirmLoading={swap.isPending}
        onCancel={() => {
          setSwapReasonFor(null);
          setSwapReason('');
        }}
        onOk={() => {
          if (swapReasonFor) {
            swap.mutate({
              assignmentId: swapReasonFor.assignmentId,
              officerId: swapReasonFor.officerId,
              ...(swapReason.trim() ? { reason: swapReason.trim() } : {}),
            });
            // ⚠️ [C1a §3] Same eviction as the unconfirmed path — see `rememberPick`.
            rememberPick(swapReasonFor.slotRef, swapReasonFor.officer);
          }
          setSwapReasonFor(null);
          setSwapReason('');
        }}
        data-testid="swap-reason-modal"
      >
        <p className="mb-3 text-sm">{DUTY_FILL_COPY.swapIntro}</p>
        <Input.TextArea
          aria-label={DUTY_FILL_COPY.swapLabel}
          placeholder={DUTY_FILL_COPY.swapPlaceholder}
          value={swapReason}
          rows={2}
          maxLength={300}
          onChange={(event) => setSwapReason(event.target.value)}
        />
      </Modal>

      {/*
        ─────────────────────────────────────────────────────────────────────────────────
         ⚠️ [C3c.2] THE SHORT-FALL DIALOG — a controlled antd `Modal`, cloned from the swap
         reason above it and NOT `modal.confirm`.

         [P2]'s rule: a required, validated input needs a controlled `<Modal>`; `modal.confirm`
         cannot hold one. `PublishPolicyModal` and the swap dialog are the standing precedents,
         and this file already imports everything it needs.

         ⚠️ THE REASON IS REQUIRED, WHICH IS THE ONE THING THAT DIFFERS FROM THE SWAP DIALOG.
         `completeShareSchema.shortfallReason` is `.trim().min(1)`, so a blank submission is a
         422 — the OK button refuses it here instead, on the trimmed value, so a modal full of
         spaces cannot get past.

         ⚠️ AND THE MODAL IS IN `e2e/fixtures/overlays.ts` IN THE SAME COMMIT. `census.spec.ts`
         fails on a STALE count as loudly as on an unaccounted overlay, and [C3c.1 §6a] took this
         file's Modal count from 2 to 1 when it deleted the control this restores.
        ─────────────────────────────────────────────────────────────────────────────────
      */}
      <Modal
        open={shortfallOpen}
        title={DUTY_FILL_COPY.shortfallTitle}
        okText={DUTY_FILL_COPY.shortfallConfirm}
        okButtonProps={{ disabled: shortfallReason.trim() === '', 'data-testid': 'shortfall-confirm' }}
        confirmLoading={completeShortfall.isPending}
        onCancel={() => {
          setShortfallOpen(false);
          setShortfallReason('');
        }}
        onOk={() => {
          const reason = shortfallReason.trim();
          if (reason === '') return;
          completeShortfall.mutate(reason);
          setShortfallOpen(false);
          setShortfallReason('');
        }}
        data-testid="shortfall-reason-modal"
      >
        <p className="mb-3 text-sm">{DUTY_FILL_COPY.shortfallIntro(openShares)}</p>
        <Input.TextArea
          aria-label={DUTY_FILL_COPY.shortfallLabel}
          placeholder={DUTY_FILL_COPY.shortfallPlaceholder}
          value={shortfallReason}
          rows={3}
          maxLength={300}
          onChange={(event) => setShortfallReason(event.target.value)}
        />
      </Modal>
    </div>
  );
};

export default DutyFillPage;
